var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import styled, { css } from '../../styled';
import { Tick } from '../icons/solid/Tick';
import Text from '../text';
const TextWrapper = styled.div `
  word-break: break-word;
`;
const InterminateRectangle = styled.div `
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 2px;
  background-color: ${({ theme, checkboxKey }) => theme.colors[theme.checkbox.types[checkboxKey].rectangle.color]};
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  visibility: hidden;
`;
const Checkbox = styled.div `
  width: 1rem;
  height: 1rem;
  border-radius: 4px;
  border: 0.0625rem solid
    ${({ theme, checkboxKey }) => theme.colors[theme.checkbox.types[checkboxKey].border]};
  box-sizing: border-box;
  background-color: ${({ theme, checkboxKey }) => theme.colors[theme.checkbox.types[checkboxKey].background]};
  display: inline-flex;
  position: relative;
  align-self: center;

  > svg {
    visibility: hidden;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    fill: ${({ theme, checkboxKey }) => theme.colors[theme.checkbox.types[checkboxKey].svg.fill]};
  }
`;
const HiddenInput = styled.input `
  display: none;
  ${({ disabled, isError, checkboxKey }) => !disabled &&
    !isError &&
    css `
      :hover {
        :not(:checked) + ${Checkbox} {
          border: 0.0625rem solid
            ${({ theme }) => theme.colors[theme.checkbox.types[checkboxKey].activeBorder]};
          > svg {
            fill: ${({ theme }) => theme.colors[theme.checkbox.types[checkboxKey].svg.hover]};
          }
        }
        :checked + ${Checkbox} {
          > svg {
            fill: ${({ theme }) => theme.colors[theme.checkbox.types[checkboxKey].svg.hover]};
          }
          > ${InterminateRectangle} {
            background-color: ${({ theme }) => theme.colors[theme.checkbox.types[checkboxKey].rectangle.hover]};
          }
        }
        ~ ${TextWrapper} span {
          color: ${({ theme }) => theme.colors[theme.checkbox.types[checkboxKey].text.hover]};
        }
      }
    `}
`;
const CheckboxWrapper = styled.label `
  display: inline-flex;
  align-items: flex-start;
  cursor: pointer;
  outline: none;
  height: 1.25rem;
  vertical-align: middle;
  span {
    display: inline-flex;
    max-width: 7.5rem;
    color: ${({ theme, checkboxKey }) => theme.colors[theme.checkbox.types[checkboxKey].text.color]};
    :not(:empty) {
      padding-left: 0.5rem;
    }
  }

  ${({ disabled, checkboxKey }) => disabled &&
    css `
      cursor: not-allowed;
      color: ${({ theme }) => theme.colors[theme.checkbox.types[checkboxKey].disabledColor]};
      ${Checkbox} {
        border: 0.0625rem solid
          ${({ theme }) => theme.colors[theme.checkbox.types[checkboxKey].disabledBorderColor]};
        background-color: ${({ theme }) => theme.colors[theme.checkbox.types[checkboxKey].disabledBackgroundColor]};
      }
      span {
        color: ${({ theme }) => theme.colors[theme.checkbox.types[checkboxKey].disabledColor]};
      }
    `};

  ${props => props.isError &&
    css `
      ${Checkbox} {
        border: 0.0625rem solid
          ${({ theme }) => theme.colors[theme.checkbox.isError.border]};
        background-color: ${({ theme }) => theme.colors[theme.checkbox.isError.background]};
        > svg {
          fill: ${({ theme }) => theme.colors[theme.checkbox.isError.color]};
        }
      }
      span {
        color: ${({ theme }) => theme.colors[theme.checkbox.isError.color]};
      }
      > input:checked + ${Checkbox} {
        > svg {
          visibility: inherit;
        }
      }
    `};

  ${({ interminate, isError, checkboxKey }) => interminate &&
    !isError &&
    css `
      > input:checked + ${Checkbox} {
        border: 0.0625rem solid
          ${({ theme }) => theme.colors[theme.checkbox.types[checkboxKey].activeBorder]};
        background-color: ${({ theme }) => theme.colors[theme.checkbox.types[checkboxKey].activeBackground]};
        > ${InterminateRectangle} {
          visibility: inherit;
        }
      }
    `};

  ${({ interminate, isError, checkboxKey }) => !interminate &&
    !isError &&
    css `
      > input:checked + ${Checkbox} {
        border: 0.0625rem solid
          ${({ theme }) => theme.colors[theme.checkbox.types[checkboxKey].activeBorder]};
        background-color: ${({ theme }) => theme.colors[theme.checkbox.types[checkboxKey].activeBackground]};
        > svg {
          visibility: inherit;
        }
      }
    `};

  ${({ disabled, isError, checkboxKey }) => !disabled &&
    !isError &&
    css `
      &:focus {
        ${Checkbox} {
          border: 0.0625rem solid
            ${({ theme }) => theme.colors[theme.checkbox.types[checkboxKey].activeBorder]};
        }
      }
    `};
`;
export const checkboxGenerator = (checkboxKey) => (_a) => {
    var { isError, interminate, label, disabled, innerRef, lang = 'en' } = _a, restProps = __rest(_a, ["isError", "interminate", "label", "disabled", "innerRef", "lang"]);
    const TextLabel = () => (React.createElement(TextWrapper, null,
        React.createElement(Text, { fontType: "Body", lang: lang, color: "INK_600" }, label)));
    return (React.createElement(CheckboxWrapper, { isError: isError, interminate: interminate, disabled: disabled, tabIndex: disabled ? -1 : 0, checkboxKey: checkboxKey },
        React.createElement(HiddenInput, Object.assign({ type: "checkbox", disabled: disabled, isError: isError, ref: innerRef, readOnly: !restProps.onChange && restProps.checked !== undefined }, restProps, { onClick: e => e.stopPropagation(), checkboxKey: checkboxKey })),
        React.createElement(Checkbox, { checkboxKey: checkboxKey },
            React.createElement(Tick, { size: "SMALL" }),
            React.createElement(InterminateRectangle, { checkboxKey: checkboxKey })),
        React.createElement(TextLabel, null)));
};
export const Default = React.forwardRef((props, ref) => {
    const CheckboxComponent = React.useMemo(() => checkboxGenerator('Default'), []);
    return React.createElement(CheckboxComponent, Object.assign({ innerRef: ref }, props));
});
export const Primary = React.forwardRef((props, ref) => {
    const CheckboxComponent = React.useMemo(() => checkboxGenerator('Primary'), []);
    return React.createElement(CheckboxComponent, Object.assign({ innerRef: ref }, props));
});
export const Basic = React.forwardRef((props, ref) => {
    const CheckboxComponent = React.useMemo(() => checkboxGenerator('Basic'), []);
    return React.createElement(CheckboxComponent, Object.assign({ innerRef: ref }, props));
});
//# sourceMappingURL=Checkbox.js.map